import { useEffect, useState, FormEvent } from 'react';
import { motion } from 'framer-motion';
import { apiRequest, getAuthUser, getApiCacheSync, invalidateApiCache, type BackendPost } from '../lib/api';

type SocialLink = {
  label: string;
  value: string;
  link: string;
  visible: boolean;
};

type AboutData = {
  eyebrow: string;
  title: string;
  description: string;
  contactEyebrow: string;
  contactTitle: string;
  social: {
    zalo: SocialLink;
    facebook: SocialLink;
    tiktok: SocialLink;
    shopee: SocialLink;
    youtube: SocialLink;
  };
};

const defaultData: AboutData = {
  eyebrow: "GIỚI THIỆU",
  title: "Về 3DSmart & Liên hệ",
  description: "3DSmart là mô hình kinh doanh tập trung vào công nghệ in 3D, hỗ trợ người dùng từ khâu lựa chọn thiết bị, cập nhật firmware, tùy chỉnh cấu hình cho tới giải quyết sự cố kỹ thuật.\n\nMục tiêu của chúng tôi là tạo ra một nền tảng vừa có tính thương mại, vừa giúp khách hàng tiếp cận thông tin kỹ thuật rõ ràng, dễ hiểu và đáng tin cậy hơn.",
  contactEyebrow: 'KẾT NỐI CÙNG 3D SMART',
  contactTitle: 'Bạn cần hỗ trợ?',
  social: {
    zalo: { label: 'Zalo', value: '0900 000 000', link: 'https://zalo.me/', visible: true },
    facebook: { label: 'Facebook', value: '3DSmart', link: 'https://facebook.com/', visible: true },
    tiktok: { label: 'TikTok', value: '@3dsmart', link: 'https://tiktok.com/', visible: true },
    shopee: { label: 'Shopee', value: '3DSmart Shop', link: 'https://shopee.vn/', visible: true },
    youtube: { label: 'YouTube', value: '3DSmart Channel', link: 'https://youtube.com/', visible: true },
  }
};

export default function AboutPage() {
  const user = getAuthUser();
  const cachedData = getApiCacheSync<{ posts: BackendPost[] }>('/posts');
  const cachedAbout = cachedData?.posts.find(p => p.slug === 'about-page') || null;
  const initialData = cachedAbout ? { ...defaultData, ...JSON.parse(cachedAbout.content), social: { ...defaultData.social, ...(JSON.parse(cachedAbout.content).social || {}) } } : defaultData;

  const [post, setPost] = useState<BackendPost | null>(cachedAbout);
  const [isEditing, setIsEditing] = useState(false);
  const [data, setData] = useState<AboutData>(initialData);
  const [formData, setFormData] = useState<AboutData>(initialData);
  const [loading, setLoading] = useState(!cachedAbout);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState('');

  useEffect(() => {
    if (isEditing) {
      const prevOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = prevOverflow;
      };
    }
  }, [isEditing]);

  useEffect(() => {
    async function loadPost() {
      try {
        const response = await apiRequest<{ posts: BackendPost[] }>('/posts', { useCache: true });
        const aboutPost = response.posts.find(p => p.slug === 'about-page');
        if (aboutPost) {
          setPost(aboutPost);
          const parsed = JSON.parse(aboutPost.content);
          const finalData = {
            ...defaultData,
            ...parsed,
            social: {
              ...defaultData.social,
              ...(parsed.social || {})
            }
          };
          setData(finalData);
          setFormData(finalData);
        }
      } catch (error) {
        // ignore JSON parse errors or network errors
      } finally {
        setLoading(false);
      }
    }
    loadPost();
  }, []);

  async function onSave(event: FormEvent) {
    event.preventDefault();
    setSaving(true);
    setStatus('Đang lưu...');
    try {
      const payload = {
        title: 'Giới thiệu',
        slug: 'about-page',
        excerpt: 'Trang giới thiệu 3DSmart',
        content: JSON.stringify(formData),
        category: 'Trang',
      };
      let updatedPost: BackendPost;
      if (post) {
        const res = await apiRequest<{ post: BackendPost }>(`/posts/${post.id}`, { method: 'PUT', body: JSON.stringify(payload) }, true);
        updatedPost = res.post;
      } else {
        const res = await apiRequest<{ post: BackendPost }>('/posts', { method: 'POST', body: JSON.stringify(payload) }, true);
        updatedPost = res.post;
      }
      setPost(updatedPost);
      setData(formData);
      setIsEditing(false);
      setStatus('');
      invalidateApiCache('/posts');
    } catch (error) {
      setStatus('Lỗi: Không thể lưu nội dung');
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return <div style={{ minHeight: '100vh' }} />;
  }

  return (
    <div className="page-block">
      <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: false, amount: 0.2 }} transition={{ duration: 0.5 }} className="section-heading">
        <span className="eyebrow">{data.eyebrow || 'GIỚI THIỆU'}</span>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '8px' }}>
          <h1 style={{ 
            margin: 0, 
            color: '#ffffff',
            flex: 1,
            minWidth: 0,
            marginRight: '12px',
            wordBreak: 'break-word',
          }}>
            {data.title || 'Về 3D SMART'}
          </h1>
          {user?.role === 'ADMIN' && (
            <button className="admin-action-btn" style={{ flexShrink: 0 }} onClick={() => {
              if (!isEditing) setFormData(data);
              setIsEditing(!isEditing);
            }}>
              <span className="icon">✎</span>
              {isEditing ? 'Hủy' : 'Chỉnh sửa'}
            </button>
          )}
        </div>
      </motion.div>

      {status && <p className="auth-status">{status}</p>}

      {isEditing && (
        <div className="modal-overlay" onClick={() => setIsEditing(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '800px' }}>
            <button className="modal-close" onClick={() => setIsEditing(false)}>×</button>
            <h2>Sửa thông tin liên hệ</h2>
            <form onSubmit={onSave} className="admin-form">
              <label className="field-label">Thẻ (Eyebrow)</label>
              <input className="input-box" value={formData.eyebrow || ''} onChange={e => setFormData({ ...formData, eyebrow: e.target.value })} />
              
              <label className="field-label" style={{ marginTop: '16px' }}>Tiêu đề trang</label>
              <input className="input-box" value={formData.title || ''} onChange={e => setFormData({ ...formData, title: e.target.value })} />

              <label className="field-label" style={{ marginTop: '16px' }}>Mô tả giới thiệu</label>
              <textarea 
                className="input-box" 
                value={formData.description} 
                onChange={e => setFormData({ ...formData, description: e.target.value })}
                style={{ minHeight: '140px', resize: 'vertical' }}
              />

              <label className="field-label" style={{ marginTop: '16px' }}>Thẻ phần liên hệ</label>
              <input className="input-box" value={formData.contactEyebrow || ''} onChange={e => setFormData({ ...formData, contactEyebrow: e.target.value })} />

              <label className="field-label" style={{ marginTop: '16px' }}>Tiêu đề phần liên hệ</label>
              <input className="input-box" value={formData.contactTitle || ''} onChange={e => setFormData({ ...formData, contactTitle: e.target.value })} />
              
              <label className="field-label" style={{ marginTop: '24px', marginBottom: '12px', fontSize: '1.1rem', color: 'var(--neon-red)', borderBottom: '1px solid var(--glass-border)', paddingBottom: '8px' }}>Cấu hình Mạng Xã Hội</label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {(Object.keys(formData.social) as Array<keyof typeof formData.social>).map((k) => {
                  const item = formData.social[k];
                  return (
                    <div key={k} style={{ display: 'flex', gap: '12px', alignItems: 'center', background: 'rgba(255,255,255,0.02)', padding: '12px', borderRadius: '12px', border: '1px solid var(--glass-border)' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: '100px', fontWeight: 'bold', cursor: 'pointer', color: '#f1f5f9' }}>
                        <input type="checkbox" checked={item.visible} onChange={e => setFormData({ ...formData, social: { ...formData.social, [k]: { ...item, visible: e.target.checked } } })} />
                        {item.label}
                      </label>
                      <input className="input-box" placeholder="Giá trị hiển thị (VD: @3dsmart)" value={item.value} onChange={e => setFormData({ ...formData, social: { ...formData.social, [k]: { ...item, value: e.target.value } } })} style={{ flex: 1, margin: 0 }} />
                      <input className="input-box" placeholder="Link URL" value={item.link} onChange={e => setFormData({ ...formData, social: { ...formData.social, [k]: { ...item, link: e.target.value } } })} style={{ flex: 2, margin: 0 }} />
                    </div>
                  );
                })}
              </div>
              
              <div className="modal-actions" style={{ marginTop: '32px' }}>
                <button className="button outline" type="button" onClick={() => setIsEditing(false)}>Hủy bỏ</button>
                <button className="button primary" type="submit" disabled={saving}>Lưu thay đổi</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <motion.div 
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: false, amount: 0.2 }}
        transition={{ duration: 0.6, ease: 'easeOut' }}
        className="about-intro" 
        style={{ 
          position: 'relative',
          marginBottom: '48px', 
          padding: '48px 48px', 
          borderRadius: '24px', 
          background: 'linear-gradient(135deg, rgba(24, 28, 38, 0.85) 0%, rgba(14, 16, 22, 0.95) 100%)',
          backdropFilter: 'blur(20px)',
          border: '1px solid var(--glass-border)',
          boxShadow: '0 20px 40px -10px rgba(0, 0, 0, 0.6), inset 0 1px 0 rgba(255, 255, 255, 0.08)',
          overflow: 'hidden'
        }}
      >
        <div style={{
          position: 'absolute', top: '-50%', right: '-10%', width: '400px', height: '400px',
          background: 'radial-gradient(circle, rgba(239, 68, 68, 0.12) 0%, transparent 70%)',
          borderRadius: '50%', zIndex: 0, pointerEvents: 'none'
        }} />
        <div style={{
          position: 'absolute', bottom: '-40%', left: '-10%', width: '350px', height: '350px',
          background: 'radial-gradient(circle, rgba(56, 189, 248, 0.08) 0%, transparent 70%)',
          borderRadius: '50%', zIndex: 0, pointerEvents: 'none'
        }} />
        <div style={{ position: 'relative', zIndex: 1, color: '#cbd5e1', fontSize: '1.15rem', lineHeight: '2', whiteSpace: 'pre-wrap', fontWeight: '400', textAlign: 'justify' }}>
          {data.description}
        </div>
      </motion.div>
      
      <motion.section 
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: false, amount: 0.2 }}
        transition={{ duration: 0.6, ease: 'easeOut' }}
        className="contact-section" 
        aria-labelledby="contact-title"
      >
        <div className="contact-heading" style={{ marginBottom: '28px' }}>
          <div>
            <span className="eyebrow" style={{ display: 'inline-block', marginBottom: '12px' }}>{data.contactEyebrow || 'KẾT NỐI CÙNG 3D SMART'}</span>
            <h2 id="contact-title" style={{ fontSize: '2.2rem', color: '#ffffff', margin: 0 }}>{data.contactTitle || 'Bạn cần hỗ trợ?'}</h2>
          </div>
        </div>
        <div className="contact-grid">
          {Object.values(data.social).filter(s => s.visible).map(s => (
            <a 
              key={s.label} 
              href={s.link} 
              target="_blank" 
              rel="noreferrer" 
              className="social-card glass-card" 
              data-platform={s.label}
              onMouseEnter={e => {
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.borderColor = 'var(--neon-red)';
                e.currentTarget.style.boxShadow = '0 12px 28px -4px rgba(239, 68, 68, 0.2)';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.transform = 'none';
                e.currentTarget.style.borderColor = 'var(--glass-border)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <span className="social-icon" aria-hidden="true">
                {s.label.charAt(0)}
              </span>
              <div className="social-copy">
                <p className="social-label">{s.label}</p>
                <p className="social-value" title={s.value}>{s.value}</p>
              </div>
              <span className="social-arrow" aria-hidden="true">↗</span>
            </a>
          ))}
        </div>
      </motion.section>
    </div>
  );
}

